/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit, Input, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, share, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { FieldKinds, RegisterViewColumnsSetting, User } from '@site-mate/dashpivot-shared-library';

import { appControlType } from 'app/apps/app-control-type.model';
import { RegisterViewFilterService } from 'app/form-fields/common/register/filters/register-view-filter.service';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { TemplateFormsCount } from 'app/shared/model/template-forms-count.model';
import { TemplateWeb } from 'app/shared/model/template.model';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { ToastrService } from 'app/shared/service/toastr.service';

import { FormsRegister } from './forms-register';
import { FormService } from '../../../form/form.service';
import { MultiSelectFilterOption } from '../../../form-fields/common/register/filters/multi-select/multi-select-filter-option';
import { ErrorHandler } from '../../../shared/service/error-handler.service';
import { TemplatesService } from '../../templates.service';

type FormId = string;

@Component({
  selector: 'cc-register-view',
  templateUrl: 'form-register-view.component.html',
  styleUrls: ['form-register-view.component.scss'],
})
export class FormRegisterViewComponent implements OnInit, OnDestroy {
  @Input() selectedForms = new Set<FormId>();
  @Input() selectAll = { selected: false };
  @Input() filterCount: { value: number };
  @Input() canAdd: boolean;

  public appControlType = appControlType;
  public fieldKinds = FieldKinds;
  public loading: boolean;
  public loadingMore: boolean;
  public register: FormsRegister;

  private buffer = 5;
  private limit = 18;
  private currentObservedChild: Element;
  private intersectionObserver: IntersectionObserver;
  private expandedRows = new Set<FormId>();
  private template: TemplateWeb;
  private unsubscribe = new Subject<void>();
  private formsCount = null;
  private readonly formsSortThreshold = 2000;

  readonly labels = {
    selectSingleForm: '',
    deselectSingleForm: '',
    selectAllForms: '',
    deselectAllForms: '',
    expandSingleForm: '',
    contractSingleForm: '',
  };

  constructor(
    private readonly errorHandler: ErrorHandler,
    private readonly formService: FormService,
    private readonly i18nService: TmpI18NService,
    private readonly registerViewFilterService: RegisterViewFilterService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly routeParamsService: RouteParamsService,
    private readonly templatesService: TemplatesService,
    private readonly toastrService: ToastrService,
  ) {}

  ngOnInit(): void {
    this.loading = true;

    this.loadRegisterView();
    this.initRegisterViewSettingEvent();
    this.fetchDataOnPageReload();
    this.initResetRegisterViewFilter();
    const labelBase = 'formsRegister';
    this.labels.selectSingleForm = this.i18nService.getMessage(`${labelBase}SelectSingleForm`);
    this.labels.deselectSingleForm = this.i18nService.getMessage(`${labelBase}DeselectSingleForm`);
    this.labels.selectAllForms = this.i18nService.getMessage(`${labelBase}SelectAllForms`);
    this.labels.deselectAllForms = this.i18nService.getMessage(`${labelBase}DeselectAllForms`);
    this.labels.expandSingleForm = this.i18nService.getMessage(`${labelBase}ExpandSingleForm`);
    this.labels.contractSingleForm = this.i18nService.getMessage(`${labelBase}ContractSingleForm`);
  }

  ngOnDestroy(): void {
    this.registerViewFilterService.clearRegisterViewFiltersCache();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  @ViewChild('scroll') public set virtualScroll(
    virtualScroller: VirtualScrollerComponent & { contentElementRef: ElementRef },
  ) {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    if (!virtualScroller) {
      return;
    }
    const contentParent = virtualScroller.contentElementRef.nativeElement;

    if (!contentParent || !contentParent.children.length) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      ([intersectionEntry]) => {
        if (intersectionEntry.isIntersecting) {
          void this.loadMoreForms();
        }
      },
      { threshold: 0.1 },
    );
    this.observeChildWithBuffer(contentParent);
  }

  public filtersChanged() {
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.register.filterParams,
      state: { reload: true },
    });
  }

  public removeSelectedForms() {
    this.register.removeForms([...this.selectedForms]);
  }

  public setLoadingState(state: boolean) {
    this.loading = state;
  }

  public handleSelectAll() {
    if (this.selectAll.selected) {
      this.selectAll.selected = false;
      this.selectedForms.clear();
    } else {
      this.selectAll.selected = true;
      this.register.columns[0].fields.forEach(({ formId }) => {
        this.selectedForms.add(formId);
      });
    }
  }

  public handleSelectForm(formId: string) {
    if (this.selectedForms.has(formId)) {
      this.selectAll.selected = false;
      this.selectedForms.delete(formId);
    } else {
      this.selectedForms.add(formId);
    }
  }

  public handleExpandRow(formId: string) {
    if (this.expandedRows.has(formId)) {
      this.expandedRows.delete(formId);
    } else {
      this.expandedRows.add(formId);
    }
  }

  public handleRowClick(formId: string) {
    return this.router.navigate([formId], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
    });
  }

  public get hasLoadedNonEmptyRegister() {
    return this.register && !this.register.isEmpty && !this.loading;
  }

  private observeChildWithBuffer(contentParent: Element) {
    if (this.currentObservedChild) {
      this.intersectionObserver.unobserve(this.currentObservedChild);
    }
    const rowsInFirstColumn = contentParent.children[0].children;
    const childIndex = Math.max(rowsInFirstColumn.length - this.buffer, 0);
    this.currentObservedChild = rowsInFirstColumn[childIndex];
    this.intersectionObserver.observe(this.currentObservedChild);
  }

  private templateOverFormsSortThreshold() {
    return this.formsCount >= this.formsSortThreshold;
  }

  private loadRegisterView() {
    this.routeParamsService
      .get('template')
      .pipe(
        takeUntil(this.unsubscribe),
        take(1),
        switchMap(this.refetchTemplateWhenMissing.bind(this)),
        tap(this.setTemplate.bind(this)),
        tap(this.setTemplateOnParamService.bind(this)),
        switchMap(this.getColumnsSettings.bind(this)),
        filter((settings) => Boolean(settings)),
        take(1),
        tap(this.buildRegisterWithSettings.bind(this)),
        switchMap(() => this.evaluateFormCountThreshold(this.template)),
      )
      .subscribe(() => {
        this.initGeneralFilters();
        void this.loadForms();
      });
  }

  private refetchTemplateWhenMissing(template: TemplateWeb) {
    const hasFullTemplate = Boolean(template?.isFullTemplate && template?._id === this.template?._id);
    if (hasFullTemplate) {
      return of(template);
    }
    const templateId = template?._id || this.route.snapshot.paramMap.get('templateId');
    return this.templatesService.byId(templateId);
  }

  private setTemplate(template: TemplateWeb) {
    this.template = template as TemplateWeb;
  }

  private setTemplateOnParamService(template: TemplateWeb) {
    this.routeParamsService.set('template', { ...template, isFullTemplate: true });
  }

  private getColumnsSettings(template: TemplateWeb) {
    return this.templatesService.getRegisterViewColumnsSetting(template.id || template._id);
  }

  private buildRegisterWithSettings(settings: RegisterViewColumnsSetting) {
    this.template.registerViewColumnsSetting = settings;
    this.register = new FormsRegister(this.template as any, this.formService);
  }

  private evaluateFormCountThreshold(retrievedTemplate) {
    return !retrievedTemplate.isOrganisationTemplate
      ? of({})
      : of(retrievedTemplate).pipe(
          switchMap((template) => {
            this.loading = true;
            const [parentFolder] = template.parents;
            return this.templatesService.getCompanyTemplatesFormsCount(parentFolder);
          }),
          tap(this.setTemplateFormCount.bind(this)),
          tap(this.displayToastIfOverThreshold.bind(this)),
        );
  }

  private displayToastIfOverThreshold() {
    if (this.templateOverFormsSortThreshold()) {
      const message = `This register is sorted by creation date due to having more than ${this.formsSortThreshold} forms.`;
      this.toastrService.warn(message);
    }
  }

  private setTemplateFormCount(formCountList: TemplateFormsCount[]) {
    const templateFormCount = formCountList.find((item) => item._id === this.template._id);
    this.formsCount = templateFormCount ? templateFormCount.formsCount : 0;
  }

  private fetchDataOnPageReload() {
    this.router.events
      .pipe(
        takeUntil(this.unsubscribe),
        filter((event) => event instanceof NavigationEnd),
        filter(() => {
          const { state } = this.router.getCurrentNavigation().extras;
          return state && state.reload;
        }),
      )
      .subscribe(() => {
        this.registerViewFilterService.clearRegisterViewFiltersCache();
        this.template = null;
        this.loadRegisterView();
      });
  }

  private initRegisterViewSettingEvent() {
    this.templatesService.registerViewSettingEvent.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.loadRegisterView();
    });
  }

  private initResetRegisterViewFilter() {
    this.formService.resetRegisterViewFilterEvent
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.router.navigate([], { relativeTo: this.route, state: { reload: true } }));
  }

  private initGeneralFilters() {
    const params = this.route.snapshot.queryParamMap;

    const filters = new Map<string, any>();
    params.keys
      .filter((parameter) => params.get(parameter))
      .forEach((key) => {
        try {
          filters.set(key, JSON.parse(params.get(key)));
        } catch (e) {
          // no-op
        }
      });

    this.register.setFilterMap(filters);
    this.filterCount.value = this.register.filterCount;
    this.initHeaderFilterOptions();
  }

  private initHeaderFilterOptions() {
    const users$ = this.getUsers();
    const [projects$, teams$] = this.getProjectsAndTeams();
    this.register.columns.forEach(({ header }) => {
      if (header.id === 'createdBy') {
        users$.subscribe((opt) => {
          header.filter.options = opt;
        });
        return;
      }

      if (header.id === 'project') {
        projects$.subscribe((opt) => {
          header.filter.options = opt;
        });
        return;
      }

      if (header.id === 'team') {
        teams$.subscribe((opt) => {
          header.filter.options = opt;
        });
        return;
      }

      if (header.id === 'workflowStep') {
        header.filter.options = this.template.columns.map((column, index) => {
          return { label: column.title, value: index + 1 };
        });
      }
    });
  }

  private getUsers(): Observable<MultiSelectFilterOption[]> {
    return this.formService.getFormUsers(this.template._id).pipe(
      map((users: User[]) => {
        return users.map((user) => ({ label: user.fullName, value: user._id }));
      }),
    );
  }

  private getProjectsAndTeams(): Observable<MultiSelectFilterOption[]>[] {
    const orig$ = this.formService.getFormProjects(this.template._id).pipe(share());

    const projects$ = orig$.pipe(
      map((projects: any[]) => {
        return projects.map((project) => ({ label: project.name, value: project._id }));
      }),
    );

    const teams$ = orig$.pipe(
      map((projects: any[]) => {
        const teams = [];
        projects.forEach((project) => {
          teams.push(
            ...project.teams.map((team) => {
              return { label: team.name, value: team._id };
            }),
          );
        });

        return teams;
      }),
    );

    return [projects$, teams$];
  }

  private async loadForms() {
    this.loading = true;
    try {
      await this.fetchRows();
    } catch (error) {
      this.errorHandler.handle(error);
    } finally {
      this.loading = false;
    }
  }

  public async loadMoreForms() {
    this.loadingMore = true;
    try {
      await this.fetchRows();
    } catch (error) {
      this.errorHandler.handle(error);
    } finally {
      this.loadingMore = false;
    }
  }

  private fetchRows() {
    let params = {};

    if (this.templateOverFormsSortThreshold()) {
      params = {
        des: 'true',
        sort: 'createdAt',
      };
    }

    return this.register.fetchRows(this.limit, params);
  }
}
